import api, { handleApiResponseSafe } from '@/lib/api';
import { OperationResult } from '@/lib/operation-result';
import type {
  DashboardStatsModel,
  RecentPaymentModel,
  OverdueContractModel,
  DashboardActivity,
  DashboardPeriod,
  BackendDashboardStats,
  BackendOverdueContract,
} from './dashboard';

/**
 * Map backend DashboardStats → frontend DashboardStatsModel.
 */
function mapDashboardStats(d: BackendDashboardStats): DashboardStatsModel {
  const totalObligated = d.totalRevenue + d.outstandingBalance;
  return {
    activeContracts: d.activeContracts ?? 0,
    totalDevicesEnrolled: d.totalDevices ?? 0,
    revenueThisMonth: d.totalRevenue ?? 0,
    outstandingBalance: d.outstandingBalance ?? 0,
    overduePaymentsCount: d.defaultedContracts ?? 0,
    totalCustomers: d.totalCustomers ?? 0,
    collectionRate:
      totalObligated > 0
        ? Math.round((d.totalRevenue / totalObligated) * 100)
        : 0,
    pendingApplications: d.pendingApplications ?? 0,
  };
}

/**
 * Map backend OverdueContract → frontend OverdueContractModel.
 */
function mapOverdueContract(o: BackendOverdueContract): OverdueContractModel {
  return {
    id: o.contractId,
    contractNumber: o.contractId,
    customerName: o.customerName,
    overdueAmount: o.overdueAmount,
    daysPastDue: o.daysOverdue,
    nextPaymentDate: o.dueDate,
  };
}

export const getDashboardStats = async (): Promise<
  OperationResult<DashboardStatsModel>
> => {
  const result = await handleApiResponseSafe<BackendDashboardStats>(() =>
    api.get<OperationResult<BackendDashboardStats>>('/v1/dashboard/stats')
  );
  if (result.succeeded && result.value) {
    return { ...result, value: mapDashboardStats(result.value) };
  }
  return result as unknown as OperationResult<DashboardStatsModel>;
};

export const getOverdueContracts = async (): Promise<
  OperationResult<OverdueContractModel[]>
> => {
  const result = await handleApiResponseSafe<BackendOverdueContract[]>(() =>
    api.get<OperationResult<BackendOverdueContract[]>>(
      '/v1/dashboard/overdue',
      {
        // Resolves every contract against loan-service; slower than the 30s default on a cold cache.
        timeout: 90_000,
      }
    )
  );
  if (result.succeeded && Array.isArray(result.value)) {
    return {
      ...result,
      value: result.value.map(mapOverdueContract),
    };
  }
  return result as unknown as OperationResult<OverdueContractModel[]>;
};

/**
 * Fetch recent payments from the dashboard endpoint.
 */
export const getRecentPayments = async (): Promise<
  OperationResult<RecentPaymentModel[]>
> => {
  return handleApiResponseSafe<RecentPaymentModel[]>(() =>
    api.get<OperationResult<RecentPaymentModel[]>>(
      '/v1/dashboard/recent-payments'
    )
  );
};

/**
 * Collected vs due, collection rate and new plans for one dashboard period.
 */
export const getDashboardActivity = async (
  period: DashboardPeriod
): Promise<OperationResult<DashboardActivity>> => {
  return handleApiResponseSafe<DashboardActivity>(() =>
    api.get<OperationResult<DashboardActivity>>('/v1/dashboard/activity', {
      params: { period },
    })
  );
};
